import { createRouter, createWebHistory, type RouteLocationNormalized, type RouteRecordRaw } from 'vue-router'
import { can, session, startSession } from '@/lib/auth'
import { NAV_ITEMS, landingFor, workstationLocked } from './nav'
import type { FeatureRoutes } from './screens'

const NotPorted = () => import('./pages/NotPortedPage.vue')

/** The agent workstation: its own chunk, with the SIP stack (JsSIP) that only call takers need. */
export const loadWorkstation = () => import('@/features/agent/AgentWorkstation.vue')

/* Every feature folder's routes.ts (see screens.ts). */
const features = Object.values(import.meta.glob<{ default: FeatureRoutes }>('../features/*/routes.ts', { eager: true }))
  .map((m) => m.default)

/** Screens already ported to Vue, by nav key; the others show a notice until their turn. */
export const PORTED: Record<string, () => Promise<unknown>> = Object.assign(
  { agent: loadWorkstation },
  ...features.map((f) => f.screens ?? {}),
)

/*
 * The workstation renders outside the shell once it is ported; until then it
 * stays in the shell, so an account landing there keeps its logout, profile
 * and language controls.
 */
const layoutOf = (key: string): 'shell' | 'bare' => (key === 'agent' && PORTED.agent ? 'bare' : 'shell')

const screens: RouteRecordRaw[] = NAV_ITEMS.map((i) => ({
  path: i.to,
  component: PORTED[i.key] ?? NotPorted,
  meta: { perm: i.perm, navKey: i.key, layout: layoutOf(i.key) },
  // Without agent.work the workstation has nothing to offer, and outside the
  // shell an "access denied" would be a dead end: go home instead.
  ...(i.key === 'agent' ? { beforeEnter: () => (!session.user || can('agent.work') ? true : landingFor(can)) } : {}),
}))

/**
 * Addresses of the previous (React) interface, kept working: bookmarks, and
 * links already handed out (a TV screen's `/mur#t=…`).
 */
const LEGACY_ALIASES: Record<string, string> = {
  '/pilotage': '/dashboard', '/classement': '/leaderboard', '/utilisateurs': '/users', '/campagnes': '/campaigns',
  '/entrants': '/inbound', '/qualifications': '/dispositions', '/rappels': '/callbacks', '/rapports': '/reports',
  '/notation': '/quality', '/effectifs': '/wfm', '/technique': '/system', '/technique/incidents': '/system/incidents',
  '/technique/entonnoir': '/system/funnel', '/technique/appel': '/system/call-timeline', '/reglages': '/settings',
  '/journal': '/audit', '/points-de-vente': '/outlets', '/commandes': '/orders', '/catalogue': '/catalog',
  '/territoires': '/territories', '/listes-de-valeurs': '/picklists', '/champs': '/fields', '/profil': '/profile',
  '/mur': '/wall', '/plateforme': '/platform', '/imports': '/contacts',
}
const aliases: RouteRecordRaw[] = [
  ...Object.entries(LEGACY_ALIASES).map(([from, to]): RouteRecordRaw => ({
    path: from, redirect: (r) => ({ path: to, query: r.query, hash: r.hash }),
  })),
  // The React console answered on any sub-address of /plateforme.
  { path: '/plateforme/:rest(.*)*', redirect: '/platform' },
]

export function isStandalone(to: RouteLocationNormalized): boolean {
  const s = to.meta.standalone
  return typeof s === 'function' ? s(to) : Boolean(s)
}

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    // Resolved once logged in (App.vue): before, nobody has a permission and every account would land on /profile.
    { path: '/', component: { render: () => null }, beforeEnter: () => (session.user ? landingFor(can) : true) },
    ...screens,
    ...features.flatMap((f) => f.routes ?? []),
    { path: '/profile', component: () => import('./pages/ProfilePage.vue'), meta: { navKey: 'profile' } },
    ...aliases,
    // An address leading to no screen says so (2026-10-12): taken silently home, one believed the
    // screen asked for had become the home screen. Logged out, the login shows first, as everywhere.
    { path: '/:pathMatch(.*)*', component: () => import('./pages/NotFoundPage.vue') },
  ],
})

router.beforeEach(async (to) => {
  // The landing screen depends on the session: wait for it on the first navigation.
  await startSession()
  // A locked agent account has only the workstation: any other address leads back to it.
  if (session.user && workstationLocked(can) && to.path !== '/agent' && !isStandalone(to)) return '/agent'
  return true
})
